import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, matchPath } from 'react-router-dom';
import { LayoutDashboard, LogOut, UserCog, Layers, Megaphone, Wallet, Banknote } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { getAdminRoleLabel } from '../../config/permissions';
import {
  PROTECTED_ROUTES,
  ACCOUNT_MANAGEMENT_PATHS,
  PLATFORM_CONFIGURATION_PATHS,
  NOTIFICATION_PATHS,
  WALLET_NAVIGATION_PATHS,
  CASH_SETTLEMENT_NAVIGATION_PATHS,
  getAccountManagementNavRoutes,
  getPlatformConfigurationNavRoutes,
  getNotificationNavRoutes,
  getWalletNavRoutes,
  getCashSettlementNavRoutes,
} from '../../config/routes';
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarItem,
  SidebarGroup,
  SidebarExpandableItem,
  SidebarNavLink,
} from './Sidebar';
import Header from './Header';
import AdminWorkspace, { AdminMain } from './AdminWorkspace';
import type { BreadcrumbItem } from '../ui/Breadcrumbs';
import { Button } from '@/components/ui/shadcn';
import {
  BreadcrumbLabelProvider,
  useBreadcrumbLabelContext,
} from '../../contexts/BreadcrumbLabelContext';

interface LayoutProps {
  children: React.ReactNode;
}

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const looksLikeUuid = (value: string) => UUID_REGEX.test(decodeURIComponent(value));

const resolveDynamicSegmentLabel = (
  rawLabel: string,
  dynamicLabel: string | null,
  fallbackTitle?: string,
) => {
  if (dynamicLabel) return dynamicLabel;
  const decoded = decodeURIComponent(rawLabel);
  if (looksLikeUuid(decoded) && fallbackTitle) return fallbackTitle;
  return decoded;
};

const LayoutShell: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();
  const { label: breadcrumbDynamicLabel } = useBreadcrumbLabelContext();

  const accountNavRoutes = useMemo(
    () => getAccountManagementNavRoutes(hasPermission),
    [hasPermission],
  );

  const walletNavRoutes = useMemo(() => getWalletNavRoutes(hasPermission), [hasPermission]);

  const cashSettlementNavRoutes = useMemo(
    () => getCashSettlementNavRoutes(hasPermission),
    [hasPermission],
  );

  const isWalletSectionActive = useMemo(
    () =>
      WALLET_NAVIGATION_PATHS.some((p) => !!matchPath({ path: p, end: false }, location.pathname)),
    [location.pathname],
  );

  const isCashSettlementSectionActive = useMemo(
    () =>
      CASH_SETTLEMENT_NAVIGATION_PATHS.some(
        (p) => !!matchPath({ path: p, end: false }, location.pathname),
      ),
    [location.pathname],
  );

  const isAccountSectionActive = useMemo(
    () =>
      ACCOUNT_MANAGEMENT_PATHS.some((p) => !!matchPath({ path: p, end: false }, location.pathname)),
    [location.pathname],
  );

  const isElevated = user?.role === 'super_admin' || user?.role === 'system_admin';

  const platformNavRoutes = useMemo(
    () => getPlatformConfigurationNavRoutes(hasPermission, isElevated),
    [hasPermission, isElevated],
  );

  const isPlatformSectionActive = useMemo(
    () =>
      PLATFORM_CONFIGURATION_PATHS.some((p) => !!matchPath({ path: p, end: false }, location.pathname)),
    [location.pathname],
  );

  const notificationNavRoutes = useMemo(
    () => getNotificationNavRoutes(hasPermission, user?.role === 'super_admin' || user?.role === 'system_admin'),
    [hasPermission, user?.role],
  );

  const isNotificationSectionActive = useMemo(
    () =>
      NOTIFICATION_PATHS.some((p) => !!matchPath({ path: p, end: false }, location.pathname)),
    [location.pathname],
  );

  const navigationItems = useMemo(() => {
    const groupedPaths = new Set<string>([
      ...ACCOUNT_MANAGEMENT_PATHS,
      ...PLATFORM_CONFIGURATION_PATHS,
      ...NOTIFICATION_PATHS,
      ...WALLET_NAVIGATION_PATHS,
      ...CASH_SETTLEMENT_NAVIGATION_PATHS,
    ]);
    const isElevated =
      user?.role === 'super_admin' || user?.role === 'system_admin';
    return PROTECTED_ROUTES.filter((route) => route.meta?.nav)
      .filter((route) => !groupedPaths.has(route.path))
      .filter((route) => !route.superAdminOnly || isElevated)
      .filter((route) => !route.permission || hasPermission(route.permission))
      .map((route) => ({
        id: route.path,
        label: route.meta?.title ?? route.path,
        path: route.path,
        icon: route.meta?.icon,
      }));
  }, [hasPermission, user?.role]);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const formatSegmentLabel = (segment: string) =>
    decodeURIComponent(segment)
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (match) => match.toUpperCase());

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    // Special-case: shop detail is conceptually part of Shop Management, not nested under Agent.
    // This keeps the header breadcrumb shorter and consistent with the left nav item.
    const shopDetailMatch = matchPath({ path: '/shops/:shopId', end: true }, location.pathname);
    if (shopDetailMatch) {
      const shopId = shopDetailMatch.params.shopId ?? '';
      return [
        { label: 'Shop Management', path: '/shops' },
        ...(shopId
          ? [
              {
                label: resolveDynamicSegmentLabel(
                  shopId,
                  breadcrumbDynamicLabel,
                  'Shop details',
                ),
                path: location.pathname,
              },
            ]
          : []),
      ];
    }

    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length === 0) {
      return [{ label: 'Dashboard', path: '/dashboard' }];
    }

    return segments.map((_, index) => {
      const path = `/${segments.slice(0, index + 1).join('/')}`;
      const matchedRoute = PROTECTED_ROUTES.find((route) =>
        !!matchPath({ path: route.path, end: true }, path),
      );
      const isLast = index === segments.length - 1;
      const rawLabel = segments[index];
      const isDynamicLabel = matchedRoute?.path.split('/').pop()?.startsWith(':');

      const label =
        isLast && isDynamicLabel
          ? resolveDynamicSegmentLabel(
              rawLabel,
              breadcrumbDynamicLabel,
              matchedRoute?.meta?.title,
            )
          : (matchedRoute?.meta?.title ?? formatSegmentLabel(rawLabel));

      return { label, path };
    });
  }, [breadcrumbDynamicLabel, location.pathname]);

  return (
    <div className="flex h-screen bg-background">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)}>
        <SidebarHeader>
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">K</span>
            </div>
            <span className="font-semibold text-lg">Kiosk Admin</span>
          </div>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            {navigationItems.map(item => {
              const Icon = item.icon ?? LayoutDashboard;
              const isActive = !!matchPath({ path: item.path, end: false }, location.pathname);
              return (
                <SidebarItem
                  key={item.id}
                  icon={<Icon className="h-4 w-4" />}
                  label={item.label}
                  onClick={() => {
                    const onSameSection = !!matchPath(
                      { path: item.path, end: false },
                      location.pathname,
                    );
                    if (onSameSection) {
                      navigate({ pathname: item.path, search: '' }, { replace: true });
                    } else {
                      navigate(item.path);
                    }
                    setSidebarOpen(false);
                  }}
                  isActive={isActive}
                />
              );
            })}
            {walletNavRoutes.length > 0 && (
              <SidebarExpandableItem
                icon={<Wallet className="h-4 w-4" />}
                label="Wallets"
                expandWhenActive={isWalletSectionActive}
                sectionActive={isWalletSectionActive}
              >
                {walletNavRoutes.map((route) => {
                  const Icon = route.meta?.icon ?? LayoutDashboard;
                  return (
                    <SidebarNavLink
                      key={route.path}
                      label={route.meta?.title ?? route.path}
                      to={route.path}
                      icon={<Icon className="h-4 w-4" />}
                      onNavigate={() => setSidebarOpen(false)}
                    />
                  );
                })}
              </SidebarExpandableItem>
            )}
            {cashSettlementNavRoutes.length > 0 && (
              <SidebarExpandableItem
                icon={<Banknote className="h-4 w-4" />}
                label="Cash Settlement"
                expandWhenActive={isCashSettlementSectionActive}
                sectionActive={isCashSettlementSectionActive}
              >
                {cashSettlementNavRoutes.map((route) => {
                  const Icon = route.meta?.icon ?? LayoutDashboard;
                  return (
                    <SidebarNavLink
                      key={route.path}
                      label={route.meta?.title ?? route.path}
                      to={route.path}
                      icon={<Icon className="h-4 w-4" />}
                      onNavigate={() => setSidebarOpen(false)}
                    />
                  );
                })}
              </SidebarExpandableItem>
            )}
            {accountNavRoutes.length > 0 && (
              <SidebarExpandableItem
                icon={<UserCog className="h-4 w-4" />}
                label="Account management"
                expandWhenActive={isAccountSectionActive}
                sectionActive={isAccountSectionActive}
              >
                {accountNavRoutes.map((route) => {
                  const Icon = route.meta?.icon ?? LayoutDashboard;
                  return (
                    <SidebarNavLink
                      key={route.path}
                      label={route.meta?.title ?? route.path}
                      to={route.path}
                      icon={<Icon className="h-4 w-4" />}
                      onNavigate={() => setSidebarOpen(false)}
                    />
                  );
                })}
              </SidebarExpandableItem>
            )}
            {platformNavRoutes.length > 0 && (
              <SidebarExpandableItem
                icon={<Layers className="h-4 w-4" />}
                label="Platform configuration"
                expandWhenActive={isPlatformSectionActive}
                sectionActive={isPlatformSectionActive}
              >
                {platformNavRoutes.map((route) => {
                  const Icon = route.meta?.icon ?? LayoutDashboard;
                  return (
                    <SidebarNavLink
                      key={route.path}
                      label={route.meta?.title ?? route.path}
                      to={route.path}
                      icon={<Icon className="h-4 w-4" />}
                      onNavigate={() => setSidebarOpen(false)}
                    />
                  );
                })}
              </SidebarExpandableItem>
            )}
            {notificationNavRoutes.length > 0 && (
              <SidebarExpandableItem
                icon={<Megaphone className="h-4 w-4" />}
                label="Notifications"
                expandWhenActive={isNotificationSectionActive}
                sectionActive={isNotificationSectionActive}
              >
                {notificationNavRoutes.map((route) => {
                  const Icon = route.meta?.icon ?? LayoutDashboard;
                  return (
                    <SidebarNavLink
                      key={route.path}
                      label={route.meta?.title ?? route.path}
                      to={route.path}
                      icon={<Icon className="h-4 w-4" />}
                      onNavigate={() => setSidebarOpen(false)}
                    />
                  );
                })}
              </SidebarExpandableItem>
            )}
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <div className="flex items-center space-x-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary">
              <span className="text-sm font-bold text-primary-foreground">
                {user?.email?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{user?.email}</p>
              <p className="text-xs text-muted-foreground">{getAdminRoleLabel(user?.role)}</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={handleLogout}
              aria-label="Log out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </SidebarFooter>
      </Sidebar>

      <AdminWorkspace>
        <Header
          onMenuClick={() => setSidebarOpen(true)}
          breadcrumbs={breadcrumbs}
          onBreadcrumbClick={(path) => {
            const onSameSection = !!matchPath({ path, end: false }, location.pathname);
            if (onSameSection) {
              navigate({ pathname: path, search: '' }, { replace: true });
            } else {
              navigate(path);
            }
          }}
        />

        <AdminMain>{children}</AdminMain>
      </AdminWorkspace>
    </div>
  );
};

const Layout: React.FC<LayoutProps> = ({ children }) => (
  <BreadcrumbLabelProvider>
    <LayoutShell>{children}</LayoutShell>
  </BreadcrumbLabelProvider>
);

export default Layout;
